import React, { useMemo } from 'react';

import { Card } from '~/eds/src/components/Card/Card';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { IconColor } from '~/eds/src/types/IconColor';
import { IconType } from '~/eds/src/types/IconType';
import { ListItem } from '~/eds/src/types/ListItem';
import { agentRegistry } from '~/features/ask-anything/chat-bot-panel/AgentRegistry';
import { useWorkflowRoutingEnabled } from '~/features/ask-anything/chat-bot-panel/hooks/useWorkflowRoutingEnabled';

import { AGENT_IDS } from '../chat-bot-panel/agentIds';
import type { AgentId } from '../chat-bot-panel/agentIds';

interface AIAgentMenuProps {
  onSelectAgent: (agentId: AgentId) => void;
  onClose: () => void;
  selectedAgentId?: AgentId | null;
  searchQuery?: string;
}

export const AIAgentMenu = ({
  onSelectAgent,
  onClose,
  selectedAgentId,
  searchQuery = '',
}: AIAgentMenuProps) => {
  const isWorkflowRoutingEnabled = useWorkflowRoutingEnabled();
  const allAgents = agentRegistry.getAll();

  const availableAgents = useMemo(() => {
    return allAgents.filter((agent) => {
      if (agent.id === AGENT_IDS.WORKFLOW_ROUTING) {
        return isWorkflowRoutingEnabled;
      }
      return true;
    });
  }, [allAgents, isWorkflowRoutingEnabled]);

  const filteredAgents = useMemo(() => {
    if (!searchQuery.trim()) {
      return availableAgents;
    }

    const query = searchQuery.toLowerCase();
    return availableAgents.filter(
      (agent) =>
        agent.label.toLowerCase().includes(query) ||
        agent.description?.toLowerCase().includes(query),
    );
  }, [availableAgents, searchQuery]);

  const listItems: ListItem[] = useMemo(
    () =>
      filteredAgents.map((agent) => ({
        title: agent.label,
        description: agent.description,
        icon: agent.icon as IconType,
        iconColor: 'status.active' as IconColor,
        isSelected: selectedAgentId === agent.id,
        onClick: () => {
          onSelectAgent(agent.id as AgentId);
          onClose();
        },
      })),
    [filteredAgents, selectedAgentId, onSelectAgent, onClose],
  );

  if (listItems.length === 0) {
    return null;
  }

  return (
    <Layout direction="column" spacing={2} minW="200px">
      {listItems.map((item, index) => (
        <Card
          key={`agent-${index}`}
          mode="raised"
          size="s"
          onClick={item.onClick}
          role="button"
        >
          <Layout direction="row" align="center" spacing={3}>
            {item.icon && (
              <Layout
                align="center"
                justify="center"
                bg="status.active.secondary"
                borderRadius="50%"
                w="32px"
                h="32px"
                flex="none"
              >
                <Icon icon={item.icon} size="s" color="status.active" />
              </Layout>
            )}
            <Layout direction="column" spacing={1} flex={1}>
              <Layout align="center" justify="space-between" w="100%">
                <Text variant="body" fontWeight="regular" color="text.primary">
                  {item.title}
                </Text>
                {item.isSelected && (
                  <Layout
                    align="center"
                    justify="center"
                    bg="status.success"
                    borderRadius="50%"
                    w="16px"
                    h="16px"
                    flex="none"
                  >
                    <Icon icon="check" size="s" color="inverse.icon" />
                  </Layout>
                )}
              </Layout>
              {item.description && (
                <Text variant="body" color="text.secondary">
                  {item.description}
                </Text>
              )}
            </Layout>
          </Layout>
        </Card>
      ))}
    </Layout>
  );
};
